import type { AxiosError } from 'axios'
import axios from 'axios'

import { setLocalStorageValue, type StorageKey } from '../hooks/use-local-storage'
import { addHttpErrorBreadcrumb, addHttpRequestBreadcrumb } from '../lib/capture-sentry-error'
import { getJwtState } from '../lib/getJwtState'

import { resolveDidBaseUrl } from './resolve-api-base-url'

const BOOTSTRAP_CLIENT = axios.create({
    withCredentials: false,
})

BOOTSTRAP_CLIENT.interceptors.request.use((config) => {
    addHttpRequestBreadcrumb(config)

    return config
})

BOOTSTRAP_CLIENT.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        addHttpErrorBreadcrumb(error)

        return Promise.reject(error)
    },
)

type AccessTokenResponse = {
    access: string
    refresh: string
}

// ВРЕМЕННО: resolveDidBaseUrl() поддерживает ?apiurl=indelsb для тестов drivee-id на проде
const USER_SETTINGS_URL = `${resolveDidBaseUrl()}/api-v1`
let accessTokenPromise: null | Promise<AccessTokenResponse> = null
let refreshTokenPromise: null | Promise<AccessTokenResponse> = null

const SESSION_STORAGE_KEYS: StorageKey[] = ['access-token', 'refresh-token', 'user-id', 'user-cid']
const DID_STORAGE_KEYS: StorageKey[] = ['user-phone', 'user-token']

const syncAuthStorage = (data: AccessTokenResponse): AccessTokenResponse => {
    setLocalStorageValue('access-token', data.access)
    setLocalStorageValue('refresh-token', data.refresh)

    const jwtState = getJwtState(data.access)

    if (jwtState.payload) {
        setLocalStorageValue('user-id', String(jwtState.payload.UserID))
        setLocalStorageValue('user-cid', String(jwtState.payload.CityID))

        return data
    }

    setLocalStorageValue('user-id', '')
    setLocalStorageValue('user-cid', '')

    return data
}

/**
 * DEV-only: добавляет валидный access-token (из `make gen-jwt`) в localStorage, минуя
 * сломанный локально DID-bootstrap. В прод-сборке всё тело вырезается по `import.meta.env.DEV`.
 * Токен берётся из `accessOverride` (URL `?access=`) либо из `VITE_DEV_ACCESS_TOKEN`.
 */
export const seedDevSession = (accessOverride?: string): boolean => {
    if (!import.meta.env.DEV) {
        return false
    }

    const access = accessOverride || (import.meta.env.VITE_DEV_ACCESS_TOKEN as string | undefined)

    if (!access) {
        return false
    }

    const refresh = (import.meta.env.VITE_DEV_REFRESH_TOKEN as string | undefined) || 'dev-refresh'

    syncAuthStorage({ access, refresh })

    return true
}

export const clearAuthStorage = ({
    clearDidCredentials = true,
}: {
    clearDidCredentials?: boolean
} = {}): void => {
    for (const key of SESSION_STORAGE_KEYS) {
        setLocalStorageValue(key, '')
    }

    if (!clearDidCredentials) {
        return
    }

    for (const key of DID_STORAGE_KEYS) {
        setLocalStorageValue(key, '')
    }
}

export const getAccessToken = async (
    phone: string,
    token: string,
): Promise<AccessTokenResponse> => {
    if (accessTokenPromise) {
        return accessTokenPromise
    }

    accessTokenPromise = BOOTSTRAP_CLIENT.post<AccessTokenResponse>(USER_SETTINGS_URL + '/auth', {
        phone,
        token,
    })
        .then((response) => {
            return syncAuthStorage(response.data)
        })
        .catch((error) => {
            throw error
        })
        .finally(() => {
            accessTokenPromise = null
        })

    return accessTokenPromise
}

export const refreshAccessToken = async (refresh: string): Promise<AccessTokenResponse> => {
    if (refreshTokenPromise) {
        return refreshTokenPromise
    }

    refreshTokenPromise = BOOTSTRAP_CLIENT.post<AccessTokenResponse>(
        USER_SETTINGS_URL + '/refresh',
        {
            refresh,
        },
    )
        .then((response) => {
            return syncAuthStorage(response.data)
        })
        .catch((error) => {
            throw error
        })
        .finally(() => {
            refreshTokenPromise = null
        })

    return refreshTokenPromise
}
